import * as THREE from 'three';
import { createNameLabel } from '../utils/NameLabel.js';

class Enemy {
    constructor(scene, position, type = 'sasquatch', world = null) {
        this.scene = scene;
        this.type = type;
        this.world = world; // For terrain collision
        this.health = 60;  // 3 shots to kill (60 / 20 damage)
        this.maxHealth = 60;
        this.speed = 3;
        this.isDead = false;
        this.lastHitTime = 0; // Prevent multi-hit in same frame
        
        // Some sasquatches are FAST! (30% chance of 3x speed)
        this.isFast = Math.random() < 0.3;
        if (this.isFast) {
            this.speed = 9; // 3x speed!
        }
        
        this.attackRange = 15;
        this.projectiles = [];
        this.lastAttackTime = 0;
        this.attackCooldown = 1.2; // Faster shooting (was 2)
        this.oneShottable = false; // Becomes true during boss fights
        this.lastVoiceTime = 0;
        this.voiceCooldown = 10; // Speak every 10 seconds
        
        this.createMesh(position);
        this.createHealthBar();
    }
    
    speak() {
        const currentTime = Date.now() / 1000;
        if (currentTime - this.lastVoiceTime < this.voiceCooldown) return;
        
        this.lastVoiceTime = currentTime;
        
        // Get audio manager from game with positional audio
        if (window.game && window.game.audioManager) {
            window.game.audioManager.playVoice('sasquatch', this.mesh.position);
        }
    }
    
    createMesh(position) {
        const geometry = new THREE.BoxGeometry(1, 2, 1);
        let color;
        
        switch(this.type) {
            case 'sasquatch':
                color = 0x8B4513;
                break;
            case 'dropbear':
                color = 0x654321;
                break;
            case 'bunyip':
                color = 0x2F4F4F;
                break;
            default:
                color = 0xff0000;
        }
        
        const material = new THREE.MeshStandardMaterial({
            color: color,
            roughness: 0.8,
            emissive: this.isFast ? color : 0x000000, // Fast ones glow!
            emissiveIntensity: this.isFast ? 0.3 : 0
        });
        
        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.position.copy(position);
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        this.scene.add(this.mesh);
        
        // Add eyes (glowing yellow for fast sasquatch!)
        const eyeGeometry = new THREE.SphereGeometry(0.1, 8, 8);
        const eyeColor = this.isFast ? 0xffff00 : 0xff0000; // Yellow eyes for fast ones!
        const eyeMaterial = new THREE.MeshBasicMaterial({ color: eyeColor });
        
        const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        leftEye.position.set(-0.2, 0.5, 0.5);
        this.mesh.add(leftEye);
        
        const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        rightEye.position.set(0.2, 0.5, 0.5);
        this.mesh.add(rightEye);
        
        // Add name label (with speed indicator!)
        const labelText = this.isFast ? 'FAST SASQUATCH' : 'SASQUATCH';
        const labelColor = this.isFast ? '#ffff00' : '#ff8800'; // Yellow for fast!
        const label = createNameLabel(labelText, labelColor);
        this.mesh.add(label);
    }
    
    createHealthBar() {
        // Create health bar above enemy
        const barWidth = 1;
        const barHeight = 0.1;
        
        const barGeometry = new THREE.PlaneGeometry(barWidth, barHeight);
        const barBackMaterial = new THREE.MeshBasicMaterial({ color: 0x333333 });
        this.healthBarBack = new THREE.Mesh(barGeometry, barBackMaterial);
        this.healthBarBack.position.y = 1.3;
        this.mesh.add(this.healthBarBack);
        
        const healthGeometry = new THREE.PlaneGeometry(barWidth, barHeight);
        const healthMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
        this.healthBarFill = new THREE.Mesh(healthGeometry, healthMaterial);
        this.healthBarFill.position.z = 0.01;
        this.healthBarBack.add(this.healthBarFill);
    }
    
    updateHealthBar() {
        if (this.healthBarFill) {
            const healthPercent = Math.max(0, this.health) / this.maxHealth; // Clamp to 0
            
            this.healthBarFill.scale.x = healthPercent;
            this.healthBarFill.position.x = -(1 - healthPercent) / 2;
            
            // Flash green during boss fights
            if (this.oneShottable) {
                const flash = Math.sin(Date.now() / 100) * 0.5 + 0.5;
                this.healthBarFill.material.color.setHex(0x00ff00);
                this.healthBarFill.material.opacity = 0.5 + flash * 0.5;
                this.healthBarFill.material.transparent = true;
            } else {
                this.healthBarFill.material.color.setHex(0xff0000);
                this.healthBarFill.material.opacity = 1;
                this.healthBarFill.material.transparent = false;
            }
            
            // Make health bar always face camera
            if (window.game && window.game.camera) {
                this.healthBarBack.lookAt(window.game.camera.position);
            }
        }
    }
    
    takeDamage(amount) {
        // FORCE DAMAGE NO MATTER WHAT
        this.health = this.health - amount;
        
        // If dead, mark it
        if (this.health <= 0) {
            this.health = 0;
            this.isDead = true;
        }
        
        // Update health bar
        if (this.healthBarFill) {
            const healthPercent = this.health / this.maxHealth;
            this.healthBarFill.scale.x = healthPercent;
            this.healthBarFill.position.x = -(1 - healthPercent) / 2;
        }
    }
    
    attack(target) {
        const currentTime = Date.now() / 1000;
        if (currentTime - this.lastAttackTime < this.attackCooldown) return;
        
        this.lastAttackTime = currentTime;
        
        // Create projectile
        const geometry = new THREE.SphereGeometry(0.15, 8, 8);
        const material = new THREE.MeshStandardMaterial({
            color: 0xff4444,
            emissive: 0xff0000,
            emissiveIntensity: 1.0
        });
        
        const projectile = new THREE.Mesh(geometry, material);
        projectile.position.copy(this.mesh.position);
        projectile.position.y += 1;
        
        // Calculate direction to target's CENTER (not feet!)
        const targetHead = target.clone ? target.clone() : new THREE.Vector3(target.x, target.y, target.z);
        targetHead.y += 1.0; // Aim at player's center of mass (was 1.6, too high)
        
        const direction = new THREE.Vector3();
        direction.subVectors(targetHead, projectile.position);
        direction.normalize();
        projectile.velocity = direction.multiplyScalar(15);
        
        // RECOIL: Push sasquatch back when shooting
        const recoilDirection = direction.clone().multiplyScalar(-1); // Opposite direction
        this.mesh.position.add(recoilDirection.multiplyScalar(0.3)); // Small pushback
        
        this.projectiles.push(projectile);
        this.scene.add(projectile);
    }
    
    getTerrainHeight(position) {
        if (!this.world) return 0;
        
        const raycaster = new THREE.Raycaster();
        const rayOrigin = new THREE.Vector3(position.x, 100, position.z);
        const rayDirection = new THREE.Vector3(0, -1, 0);
        raycaster.set(rayOrigin, rayDirection);
        
        const terrainMeshes = [];
        if (this.world.island) terrainMeshes.push(this.world.island);
        if (this.world.volcano && this.world.volcano.terrain) terrainMeshes.push(this.world.volcano.terrain);
        if (this.world.beach) terrainMeshes.push(this.world.beach);
        
        const intersects = raycaster.intersectObjects(terrainMeshes, false);
        return intersects.length > 0 ? Math.max(...intersects.map(hit => hit.point.y)) : 0;
    }
    
    update(deltaTime, playerPosition, allEnemies = []) {
        // Don't update if dead!
        if (this.isDead) return;
        
        // OPTIMIZATION: Distance-based LOD (Level of Detail)
        // MULTIPLAYER-READY: In multiplayer, playerPosition could be an array of positions
        // We throttle based on CLOSEST player for smooth experience
        this.frameCount = (this.frameCount || 0) + 1;
        
        let distToClosestPlayer;
        if (Array.isArray(playerPosition)) {
            // Multiplayer: find closest player
            distToClosestPlayer = Math.min(...playerPosition.map(pos => 
                this.mesh.position.distanceTo(pos)
            ));
        } else {
            // Single player
            distToClosestPlayer = this.mesh.position.distanceTo(playerPosition);
        }
        
        if (distToClosestPlayer > 80) {
            // Very far from ALL players: update every 10 frames (6fps)
            if (this.frameCount % 10 !== 0) return;
            deltaTime *= 10;
        } else if (distToClosestPlayer > 50) {
            // Far from ALL players: update every 5 frames (12fps)
            if (this.frameCount % 5 !== 0) return;
            deltaTime *= 5;
        } else if (distToClosestPlayer > 25) {
            // Medium distance from closest player: update every 2 frames (30fps)
            if (this.frameCount % 2 !== 0) return;
            deltaTime *= 2;
        }
        // Close to ANY player (<25 units): full 60fps update
        
        // Update health bar
        this.updateHealthBar();
        
        // Speak occasionally
        if (Math.random() < 0.002) { // 0.2% chance per frame
            this.speak();
        }
        
        // Move towards player
        const direction = new THREE.Vector3();
        direction.subVectors(playerPosition, this.mesh.position);
        const distance = direction.length();
        direction.normalize();
        
        if (distance > 5) {
            // Calculate desired movement
            const desiredMove = direction.clone().multiplyScalar(this.speed * deltaTime);
            const newPos = this.mesh.position.clone().add(desiredMove);
            
            // Check collision with other enemies
            let canMove = true;
            for (const other of allEnemies) {
                if (other === this || !other.mesh) continue;
                
                const distToOther = newPos.distanceTo(other.mesh.position);
                if (distToOther < 2.5) { // Collision radius
                    canMove = false;
                    // Push away slightly
                    const pushDir = new THREE.Vector3();
                    pushDir.subVectors(this.mesh.position, other.mesh.position);
                    pushDir.normalize();
                    this.mesh.position.add(pushDir.multiplyScalar(deltaTime * 2));
                    break;
                }
            }
            
            // Move closer if no collision
            if (canMove) {
                this.mesh.position.add(desiredMove);
            }
            
            // Follow terrain height (walk over volcano!)
            const terrainHeight = this.getTerrainHeight(this.mesh.position);
            const enemyHeight = 1; // Height above ground
            if (this.mesh.position.y < terrainHeight + enemyHeight) {
                this.mesh.position.y = terrainHeight + enemyHeight;
            }
            
            // BOUNDING ANIMATION - up and down movement
            const time = Date.now() / 1000;
            const bounceSpeed = this.isFast ? 8 : 4; // Faster bounce for fast sasquatches
            const bounceHeight = 0.5;
            this.mesh.position.y += Math.sin(time * bounceSpeed) * bounceHeight * deltaTime * this.speed;
        }
        
        // Look at player
        this.mesh.lookAt(playerPosition);
        
        // Attack if in range
        if (distance < this.attackRange) {
            this.attack(playerPosition);
        }
        
        // Update projectiles (reverse loop for safe splicing)
        for (let i = this.projectiles.length - 1; i >= 0; i--) {
            const projectile = this.projectiles[i];
            if (!projectile) {
                this.projectiles.splice(i, 1);
                continue;
            }
            
            projectile.position.add(projectile.velocity.clone().multiplyScalar(deltaTime));
            
            // Remove if too far
            if (projectile.position.distanceTo(this.mesh.position) > 50) {
                this.scene.remove(projectile);
                this.projectiles.splice(i, 1);
            }
        }
    }
    
    destroy() {
        if (this.mesh) {
            this.scene.remove(this.mesh);
        }
        if (this.healthBarBack) {
            this.scene.remove(this.healthBarBack);
        }
        this.projectiles.forEach(p => {
            this.scene.remove(p);
        });
    }
}

export class EnemyManager {
    constructor(scene, player, audioManager, world = null) {
        this.scene = scene;
        this.player = player;
        this.audioManager = audioManager;
        this.world = world; // For enemy terrain collision
        this.enemies = [];
        this.spawnTimer = 0;
        this.spawnInterval = 1.5; // Spawn every 1.5 seconds (faster spawning!)
        this.maxEnemies = 25; // Increased from 10 - more on field!
        this.bossActive = false; // Track if boss is active
        
        // Spawn initial enemies
        this.spawnInitialEnemies();
    }
    
    setBossActive(active) {
        this.bossActive = active;
        
        // Make all enemies one-shottable during boss fights
        this.enemies.forEach(enemy => {
            enemy.oneShottable = active;
        });
    }
    
    spawnInitialEnemies() {
        const types = ['sasquatch', 'dropbear', 'bunyip'];
        
        for (let i = 0; i < 5; i++) {
            const angle = (i / 5) * Math.PI * 2;
            const radius = 20 + Math.random() * 10;
            const position = new THREE.Vector3(
                Math.cos(angle) * radius,
                1,
                Math.sin(angle) * radius
            );
            
            const type = types[Math.floor(Math.random() * types.length)];
            this.enemies.push(new Enemy(this.scene, position, type, this.world));
        }
    }
    
    spawnEnemy() {
        if (this.enemies.length >= this.maxEnemies) return;
        
        const types = ['sasquatch', 'dropbear', 'bunyip'];
        
        // Spawn from volcano (center at 0,0)
        const angle = Math.random() * Math.PI * 2;
        const radius = 5 + Math.random() * 10; // 5-15 units from volcano center
        const position = new THREE.Vector3(
            Math.cos(angle) * radius,
            28, // Volcano top height
            Math.sin(angle) * radius
        );
        
        const type = types[Math.floor(Math.random() * types.length)];
        this.enemies.push(new Enemy(this.scene, position, type, this.world));
    }
    
    removeEnemy(index) {
        if (this.enemies[index]) {
            this.enemies[index].destroy();
            this.enemies.splice(index, 1);
        }
    }
    
    update(deltaTime) {
        // Don't spawn enemies during boss fights
        if (!this.bossActive) {
            this.spawnTimer += deltaTime;
            if (this.spawnTimer >= this.spawnInterval) {
                this.spawnTimer = 0;
                this.spawnEnemy();
            }
        }
        
        // Update all enemies - pass all enemies for collision detection
        this.enemies.forEach(enemy => {
            enemy.update(deltaTime, this.player.mesh.position, this.enemies);
        });
    }
}
